// Does this one stop at MY station.
//
// A departure board is signed by where trains END UP. Somebody going to
// Blackburn stands at Flinders Street reading BELGRAVE, LILYDALE, BELGRAVE and
// cannot tell which of the three calls at Blackburn, because two of them run
// express through it. That is true of every line with expresses — Belgrave and
// Lilydale through Box Hill, Pakenham and Cranbourne through Carnegie and
// Oakleigh, Frankston, Werribee, Craigieburn, Mernda — and the app's own
// answer to it was "leave the board and plan a trip", when the question is
// only "is this one mine".
//
// PURE, and over four inputs: the board you are looking at, the destination's
// own board, the stop id you chose, and how far ahead a board reaches. No
// fetch, no store, no Vue — same reason `src/rules.js` and `src/severity.js`
// are pure, which is that a verdict this consequential has to be testable
// against real bundle data without a browser in the room.
//
// WHAT IT WILL NOT DO IS FETCH A PATTERN PER ROW. `/v3/pattern/run/…` is the
// exact answer and it is one request per train; a board at Flinders Street is
// thirty rows. So the answers below are ordered by what they cost, and the
// cheap ones are also the certain ones:
//
//   1. ELSEWHERE — the destination is not on this run's line at all. The
//      bundle knows this. No request, no doubt: an Alamein train does not go
//      to Blackburn and never has.
//   2. TERMINATES THERE — PTV's own `final_stop_id` is the station you chose.
//      A train that ends at Blackburn obviously calls at Blackburn, and this
//      is the one case the two-board check below gets WRONG on its own: a
//      terminating service never departs its terminus, so it is missing from
//      that station's departure board for the most innocent reason there is.
//   3. ALL STATIONS — PTV's `express_stop_count` is 0, and the destination
//      lies between here and where the train ends. A train that skips nothing
//      stops at everything on its way, which includes yours.
//   4. TWO BOARDS, INTERSECTED. For everything left — a run that skips some
//      stops, or a feed that does not publish a count — fetch the DESTINATION
//      station's own departure board and match run refs. A run on both boards
//      stops at both, exactly, and the destination row carries the time.
//
// AND THE RULE THAT GOVERNS ALL OF IT: never guess past the horizon. A
// destination board covers a fixed window (`WINDOW_MINUTES` in
// `server/ptv.mjs`, reported as `windowMinutes`). A run missing from it means
// EXPRESS only if the train would have arrived inside that window; past it,
// the board was never going to show the run and its absence says nothing. The
// answer there is UNKNOWN and the row says nothing at all.
//
// A FALSE "DOES NOT STOP" COSTS SOMEBODY A TRAIN. A blank costs nothing. Every
// unresolved case below resolves to the blank.

import {
  isCityEnd, lineStops, metresBetween, segmentSeconds, throughContinuations,
} from './network.js';
// The one import from outside network.js, and it is a number rather than a
// behaviour: the nominal speed the line strip reckons a train at. Copying the
// figure here would be a second place for it to be wrong, and `progress.js`
// already imports nothing but `network.js` itself — so this costs the chunk
// nothing it was not already paying for.
import { RECKON_SPEED_MPS } from './progress.js';

/** The four things a row can be told, and the only four. */
export const VERDICTS = ['stops', 'express', 'elsewhere', 'unknown'];

// ── How long the trip takes ──────────────────────────────────────────────────

/**
 * The timetabled run between two stations on one line, in seconds.
 *
 * Summed from `segmentSeconds`, which is the bundle's own gap data — the
 * shortest scheduled gap seen between two consecutive stops, written by
 * `scripts/fetch-network.mjs` from real run patterns. It is the same data the
 * line strip paces a train by, so there is no second model of how long a train
 * takes and no second thing to get wrong.
 *
 * WHERE A GAP IS MISSING, the chord between the two stations at
 * `RECKON_SPEED_MPS`, exactly as `reckon` in `src/progress.js` falls back. A
 * hole in the timetable data is not a reason to have no answer; it is a reason
 * to have a rougher one.
 *
 * DIRECTION-FREE, deliberately. The two indices are ordered before they are
 * walked, so this answers "how far apart are these two stations in minutes"
 * rather than "does this train go that way" — the second question is answered
 * elsewhere, by the run's own terminus and by the other board.
 *
 * Null when either station is not on the line, or when they are the same one.
 */
export function journeySeconds({ routeId, fromStopId, toStopId } = {}) {
  const stops = lineStops(routeId);
  if (!stops.length) return null;

  const from = stops.findIndex((s) => Number(s.stopId) === Number(fromStopId));
  const to = stops.findIndex((s) => Number(s.stopId) === Number(toStopId));
  if (from < 0 || to < 0 || from === to) return null;

  const lo = Math.min(from, to);
  const hi = Math.max(from, to);

  let total = 0;
  for (let i = lo; i < hi; i += 1) {
    const timetabled = segmentSeconds(routeId, i);
    if (Number.isFinite(timetabled) && timetabled > 0) {
      total += timetabled;
      continue;
    }
    const a = stops[i];
    const b = stops[i + 1];
    const metres = metresBetween(a.lat, a.lng, b.lat, b.lng);
    if (!Number.isFinite(metres) || metres <= 0) return null;
    total += metres / RECKON_SPEED_MPS;
  }
  return total;
}

/**
 * The same estimate, padded, for the one decision that must not be confident.
 *
 * `segmentSeconds` holds the SHORTEST gap seen between two stops, so summing it
 * along a line describes a train that never waits — Flinders Street to
 * Blackburn comes out at about 31 minutes against a real all-stations run of
 * 41 to 45. That understatement is harmless everywhere except the horizon test,
 * where it is the failure that matters: an arrival estimated too early is an
 * arrival wrongly judged to be INSIDE the window, and a run that was never
 * going to appear on the destination board is then called express.
 *
 * So the horizon is tested against a deliberately generous journey: half again
 * plus five minutes, which covers the observed 1.4× and then some. The cost of
 * being generous is a row near the two-hour edge saying nothing; the cost of
 * being tight is a row telling somebody their train does not stop where it
 * stops.
 */
export const ESTIMATE_SLACK = 0.5;
export const ESTIMATE_SLACK_S = 300;

/**
 * How far after a departure a far-board row has to be to be its arrival.
 *
 * One minute. Under that it is the same instant read off two boards — the
 * destination a stop away, a clock a few seconds out of step — and never a
 * journey anybody takes.
 */
export const ARRIVAL_FLOOR_MS = 60_000;

export function paddedJourneySeconds(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  return s * (1 + ESTIMATE_SLACK) + ESTIMATE_SLACK_S;
}

// ── The stopping note, with nobody's destination in it ───────────────────────

/**
 * "ALL STATIONS", "EXPRESS", or nothing.
 *
 * The one fact `express_stop_count` gives away for free, to everyone, before
 * anybody has chosen a destination at all: whether this train is a stopper.
 * It answers half the platform's questions on its own — somebody going two
 * stops out does not need to name the station to know that the all-stations
 * train will do.
 *
 * A CANCELLED TRAIN GETS NOTHING. It stops nowhere, and "ALL STATIONS" on a
 * struck-through row is the board contradicting itself.
 *
 * Silence where PTV did not say, which is Sydney's whole feed and any Melbourne
 * board read without the Run expansion.
 */
export function stoppingNote(departure) {
  if (!departure || departure.cancelled) return '';
  const count = expressCount(departure);
  if (count == null) return '';
  return count === 0 ? 'ALL STATIONS' : 'EXPRESS';
}

/**
 * The skipped-stop count as a number, or null. NEVER `Number(…)` at the call
 * site.
 *
 * `Number(null)` is 0 and `Number(undefined)` is NaN, so the obvious spelling
 * turns "PTV did not say" into "this train stops everywhere" — which is the
 * one wrong answer here that puts somebody on an express. Caught by the Sydney
 * case in `tests/stopping.test.js`, where the whole feed has no count at all.
 */
function expressCount(departure) {
  const raw = departure?.expressStopCount;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// ── What to say about it ─────────────────────────────────────────────────────

/**
 * The verdict in words, and in ONE place.
 *
 * Read twice per row — once by the eye, once by a screen reader through
 * `rowLabel` — and a second copy of these sentences is a second copy to get
 * out of step. IN WORDS, never a symbol: a ✗ beside a destination is a thing
 * somebody has to learn, and they are learning it on a platform.
 */
export function verdictPhrase(verdict, destinationName) {
  const where = String(destinationName ?? '').trim() || 'there';
  if (verdict === 'express') return `Doesn’t stop at ${where}`;
  if (verdict === 'elsewhere') return `Doesn’t go to ${where}`;
  // Not a fact about the train: a fact about what somebody asked to keep off.
  // See `avoidVerdicts` in board-plans.js.
  if (verdict === 'avoided') return 'Goes via somewhere you are avoiding';
  // Not a fact about the train either: about the ticket. See `vlineBarred`.
  if (verdict === 'vline') return 'V/Line can’t be used between metro stations';
  return '';
}

// ── The answer ───────────────────────────────────────────────────────────────

const timeOf = (row) => Date.parse(row?.estimatedAt ?? row?.scheduledAt ?? '');

/** Every route this run rides: its own, and the one it becomes past the city. */
function routesOf(departure) {
  const ids = [];
  for (const id of [departure?.routeId, departure?.throughRouteId]) {
    const n = Number(id);
    if (Number.isFinite(n) && n > 0 && !ids.includes(n)) ids.push(n);
  }
  return ids;
}

const indexOn = (stops, stopId) => stops.findIndex((s) => Number(s.stopId) === Number(stopId));

/**
 * Whether the destination MIGHT be on the far side of the city for this run.
 *
 * `throughContinuations` says what a LINE becomes past the city — since the
 * Metro Tunnel opened, a Pakenham train carries on as a Sunbury service. What
 * it cannot say is whether THIS run does: only PTV knows that, and it says so
 * by advertising a distributor, which arrives here as `throughRouteId`. When it
 * did so, the continuation is already in `routesOf` above and is checked like
 * any other line.
 *
 * When it did NOT, and the destination sits on a line this one sometimes
 * becomes, the honest answer is that we do not know. Calling it ELSEWHERE would
 * tell a commuter at Carnegie that the train to Footscray does not go to
 * Footscray, on half of a fact.
 */
function mightContinueTo(departure, destinationStopId) {
  for (const routeId of routesOf(departure)) {
    for (const cont of throughContinuations(routeId)) {
      if (cont.stops.some((s) => Number(s.stopId) === Number(destinationStopId))) return true;
    }
  }
  return false;
}

/**
 * Every ref a row answers to: its own, and the one it becomes past the city.
 *
 * A THROUGH SERVICE IS TWO RUNS JOINED AT THE TUNNEL. PTV hands a Cranbourne
 * train over to a Sunbury run at Town Hall, so the row at Carnegie carries
 * `runRef` 967540 and the SAME TRAIN's row at State Library carries 990659 —
 * the distributor's. A board intersection matching on `runRef` alone therefore
 * misses exactly the trains the Metro Tunnel was built for, which is why the
 * citybound half of a Carnegie board could be marked "doesn't stop at State
 * Library" while the far board was sitting there with the arrival on it.
 *
 * The same rule and the same two fields as `refsOf` in `src/commute.js`, which
 * is where the widget's arrival stamps already follow the handover. This file
 * had its own narrower copy of the idea and that was the bug.
 */
function refsOf(row) {
  const out = [];
  for (const ref of [row?.runRef, row?.throughRunRef]) {
    if (ref == null) continue;
    const s = String(ref).trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

// ── The passenger's journey, which is not the train's run ────────────────────
//
// PTV models a through service as two runs, and the app used to reason about
// the first one. A citybound train at Carnegie has `final_stop_id` TOWN HALL —
// the handover — and the tunnel order from Carnegie is Anzac, Town Hall, State
// Library, Parkville, Arden. So State Library, one stop past the handover, sat
// outside "between here and where this run ends" and the board dimmed every
// train that actually goes there.
//
// The passenger never moves seat. What they can reach is this line from here
// to the handover, PLUS the line the train becomes, onward from it.

/**
 * Where this train can still take somebody, from the platform they are on.
 *
 * `{ ahead, beyond, handsOver, looping }`, or null when the bundle cannot place
 * the run at all — which is a REFUSAL rather than an empty answer, and is what
 * keeps the football specials out of this: a train at Richmond on route 11 has
 * no index on a line that deliberately does not list Richmond, so this says
 * nothing and the run's own pattern is left to answer.
 *
 * `ahead` — the stops this run makes after here, in order, ending where it
 * leaves its own line. CERTAIN, in the sense that the line's stop list is the
 * order the train passes things in; whether it opens its doors at each is the
 * separate question `express_stop_count` answers.
 *
 * `beyond` — the continuation's stops past the handover. NOT certain, and used
 * only to WITHHOLD a verdict: the distributor's own terminus is not published
 * on the row, so walking the continuation to the end of its line overstates the
 * reach. Overstating is the safe direction for a thing that only ever stops the
 * board saying "doesn't stop there".
 *
 * `looping` — the run ends in the city, where the bundle's stop list cannot say
 * which way round it gets there. A Belgrave service reaching Richmond at 13:16
 * and then going round the loop calls at four city stations the list puts on
 * the far side of its terminus. Nothing negative may be said about a run like
 * that from the bundle alone.
 */
function journeyAhead(departure, originStopId) {
  const own = lineStops(Number(departure?.routeId));
  if (!own.length) return null;

  const here = indexOn(own, originStopId);
  if (here < 0) return null;

  const finalStopId = Number(departure?.finalStopId);
  if (!Number.isFinite(finalStopId) || finalStopId <= 0) return null;

  const contRouteId = Number(departure?.throughRouteId) || 0;
  let handsOver = contRouteId > 0 || Boolean(departure?.throughRunRef);

  // Where this run leaves this line. USUALLY IT IS ON IT — since the tunnel
  // timetable, a citybound Pakenham run ends at Town Hall and Town Hall is on
  // the Pakenham list. Where it is NOT, the terminus belongs to the line this
  // one becomes — a Cranbourne train "to Watergardens" — and a train that
  // carries on past the city has to reach the city first, so on its own line it
  // runs all the way to index 0.
  //
  // ONLY WHEN THE TERMINUS IS ON A LINE THIS RUN BECOMES, though. A final stop
  // the bundle cannot place anywhere is a run the bundle cannot describe, and
  // reading it as "runs to the city end" would have an outbound V/Line service
  // pointed at the city and every suburb after this one called a station it
  // does not stop at.
  let endIdx = indexOn(own, finalStopId);
  if (endIdx < 0) {
    if (!mightContinueTo(departure, finalStopId)) return null;
    endIdx = 0;
    handsOver = true;
  }
  if (endIdx === here) return null;

  const step = endIdx > here ? 1 : -1;
  const ahead = [];
  for (let i = here + step; i >= 0 && i < own.length; i += step) {
    ahead.push(own[i].stopId);
    if (i === endIdx) break;
  }

  const looping = Boolean(own[endIdx] && isCityEnd(own[endIdx]));

  // The far half of the journey, where PTV named the line it hands over to.
  //
  // WHICH WAY IT GOES FROM THE HANDOVER is not published either, and there is
  // no need for it to be: the train does not turn round. Of the two ways off
  // the join, one leads straight back to a station this run has just called at.
  // The other is the journey. Where both look new, or both look visited, the
  // continuation is left out rather than guessed at.
  const beyond = [];
  const cont = contRouteId > 0 ? lineStops(contRouteId) : [];
  const join = cont.length ? indexOn(cont, own[endIdx].stopId) : -1;
  if (join >= 0) {
    const made = new Set([own[here].stopId, ...ahead]);
    const onward = [1, -1].filter((s) => cont[join + s] && !made.has(cont[join + s].stopId));
    if (onward.length === 1) {
      for (let i = join + onward[0]; i >= 0 && i < cont.length; i += onward[0]) {
        beyond.push(cont[i].stopId);
      }
    }
  }

  return { ahead, beyond, handsOver, looping };
}

/**
 * The stations this train is carried through between here and `destStopId`,
 * strictly inside, in order — or null when the bundle cannot say.
 *
 * What the board asks when somebody has named a station to AVOID: the planner
 * refuses a train that carries you through one (`segment` in journey.js), and a
 * direct train on the board has to be refused on the same terms or the board
 * and the journey it opens would disagree. Off the line's own stop list, so it
 * costs nothing; null — a loop, a run the bundle cannot place, a destination
 * past a handover it cannot follow — means "no claim", and the row is left
 * alone rather than refused on a guess. The journey page reads the real
 * pattern when the row is opened.
 */
export function stopsPassed(departure, originStopId, destStopId) {
  const j = journeyAhead(departure, originStopId);
  // A run that ends in the city with nowhere named to go next may go round the
  // loop either way, and the order of what it passes is not known. One that
  // hands over to a named line (the tunnel's Town Hall) has a known path.
  if (!j || (j.looping && !j.beyond.length)) return null;
  const path = [...j.ahead, ...j.beyond].map(Number);
  const at = path.indexOf(Number(destStopId));
  if (at < 0) return null;
  return path.slice(0, at);
}

// ── The train's own pattern, where the bundle cannot be trusted ──────────────
//
// The four answers above are ordered by cost and the bundle's is the cheapest,
// which is right for thirty rows on a line the bundle knows. It is WRONG for a
// row whose line does not list the station it is standing at: a football
// special out of the city via Richmond and South Yarra runs on routes 4 and 11,
// whose stop lists deliberately leave both stations out because the everyday
// service is in the Metro Tunnel (`NOT_CALLED_AT` in
// `scripts/fetch-network.mjs` has the whole story). The bundle is right about
// the LINE and wrong about the TRAIN, and only the train's own pattern can say
// so.
//
// So the caller may fetch a pattern for those few rows and hand them in. Still
// never one per row — the rule in the header holds — but a handful on the six
// evenings a year it matters is not thirty every time anybody opens a board.

/**
 * One run's pattern — its stops, and whether the train carries on as another
 * run — or null where there is none.
 *
 * Takes what the app already has: `patterns` as a Map or a plain object, keyed
 * by run ref, holding either a mapped run (`{ stops: [{ stopId }], continues }`,
 * which is what `fetchRun` answers with) or a bare list of stop ids. A pattern
 * with no usable stops is a null, not an empty answer — "this run stops
 * nowhere" is never a thing to act on.
 *
 * `continues` matters as much as the stops do. PTV models a through service as
 * two runs joined at the tunnel, so a pattern that ends where the train hands
 * over is a COMPLETE pattern of an INCOMPLETE journey, and reading a missing
 * destination off it would be the old "doesn't stop at State Library" bug in a
 * new place.
 */
function patternStops(patterns, ref) {
  if (!patterns) return null;
  const held = patterns instanceof Map ? patterns.get(String(ref)) : patterns[String(ref)];
  const rows = Array.isArray(held) ? held : held?.stops;
  if (!Array.isArray(rows) || !rows.length) return null;
  const stops = rows
    .map((s) => ({
      stopId: Number(typeof s === 'object' ? s?.stopId : s),
      at: Date.parse(typeof s === 'object' ? (s?.estimatedAt ?? s?.scheduledAt ?? '') : ''),
    }))
    .filter((s) => Number.isFinite(s.stopId) && s.stopId > 0);
  if (!stops.length) return null;
  return { stops, continues: Array.isArray(held) ? null : (held?.continues ?? null) };
}

/**
 * The verdict the server's timetable match supports, or null when it had none.
 *
 * `reaches` is `{ at }` (scheduled arrival at the destination) or `false`. The
 * arrival is moved by the delay the row is running now, so a train five
 * minutes down reads five minutes later at the far end; `estimated` stays
 * false because it is a timetable time carried along, not a guess at a
 * journey length.
 *
 * `reachesTo` (stamped by `fetchDepartures`) is the destination the server was
 * asked about. Rows read for another destination, and not yet re-read, are not
 * evidence about this one.
 *
 * A train that does NOT call is "express" on a line that serves the
 * destination (Town Hall to Footscray express, past State Library's
 * neighbours) and "elsewhere" on one that does not.
 */
function timetableVerdict(departure, dest, serving, iso) {
  const reaches = departure?.reaches;
  if (reaches === undefined || reaches === null) return null;
  if (departure.reachesTo != null && Number(departure.reachesTo) !== dest) return null;
  if (reaches === false) {
    return { verdict: serving != null ? 'express' : 'elsewhere', arrivesAt: null, estimated: false };
  }
  const at = Date.parse(reaches.at ?? '');
  if (!Number.isFinite(at)) return null;
  let delay = departure.delayMinutes == null ? NaN : Number(departure.delayMinutes);
  if (!Number.isFinite(delay)) {
    delay = (Date.parse(departure.estimatedAt ?? '') - Date.parse(departure.scheduledAt ?? '')) / 60_000;
  }
  if (!Number.isFinite(delay)) delay = 0;
  return { verdict: 'stops', arrivesAt: iso(at + delay * 60_000), estimated: false };
}

/**
 * The verdict this run's own pattern supports. Never null: a complete pattern
 * answers every question about the train it belongs to.
 *
 * FOUR READINGS, in the order of what they are worth:
 *
 *   * THE DESTINATION IS AHEAD ON THE PATTERN — it stops there, and the time is
 *     the far board's row where there is one, else the run's own published time
 *     for that stop. Neither is an estimate: both are PTV's minute for this
 *     train at that platform.
 *   * THE DESTINATION IS BEHIND — the train called there before it got here,
 *     which is true and useless, and the one thing that must not be said is
 *     that it does not stop there. Nothing.
 *   * THE PATTERN HANDS OVER — this run becomes another one past the city,
 *     either by PTV's own `continues` or by what the line is known to become.
 *     The rest of the journey belongs to a run this pattern says nothing about,
 *     so neither does this. Nothing.
 *   * THE DESTINATION IS NOT ON IT AT ALL — this train does not call there.
 *     WHICH SENTENCE that is still comes from the bundle, because "skips your
 *     stop" and "goes somewhere else entirely" are different things to tell
 *     somebody: the destination on one of this run's own lines is an EXPRESS,
 *     and a destination on no line of its is ELSEWHERE.
 */
function patternVerdict(pattern, { dest, originStopId, departure, boardArrival, iso }) {
  const { stops, continues } = pattern;
  const there = stops.findIndex((s) => s.stopId === dest);
  const here = originStopId == null
    ? -1
    : stops.findIndex((s) => s.stopId === Number(originStopId));

  if (there >= 0 && (here < 0 || there > here)) {
    const own = stops[there].at;
    return boardArrival != null
      ? { verdict: 'stops', arrivesAt: iso(boardArrival), estimated: false }
      : { verdict: 'stops', arrivesAt: Number.isFinite(own) ? iso(own) : null, estimated: false };
  }
  if (there >= 0) return { verdict: 'unknown', arrivesAt: null, estimated: false };
  if (continues || mightContinueTo(departure, dest)) {
    return { verdict: 'unknown', arrivesAt: null, estimated: false };
  }
  const onItsLine = routesOf(departure).some((routeId) => indexOn(lineStops(routeId), dest) >= 0);
  return { verdict: onItsLine ? 'express' : 'elsewhere', arrivesAt: null, estimated: false };
}

/**
 * A verdict per run ref, for the board in front of somebody.
 *
 * `{ verdict, arrivesAt, estimated }`, where `arrivesAt` is an ISO string or
 * null and `estimated` says the time was reckoned from the timetable rather
 * than read off the destination's board.
 *
 * NOT EVERY ROW GETS AN ENTRY, and the omissions are the point:
 *
 *   * THE DESTINATION IS THE STATION YOU ARE AT. Nothing at all — every train
 *     here "stops here", and the chip should never have been settable to it.
 *   * A CANCELLED RUN. A cancelled train stops nowhere, and the board already
 *     says so, struck through. Marking it a second way would be two facts
 *     competing over one row.
 *   * NO RUN REF. There is nothing to match on and nothing to say.
 *
 * A RUN APPEARING TWICE gets one entry: the board is in time order, so the
 * first row is the one leaving soonest, and that is the one a person reading
 * downwards means.
 *
 * `patterns` is runRef → that run's OWN stopping pattern, for the rows where
 * the bundle is known to be no use — see `patternStops` below. It is optional,
 * usually empty, and where it answers it answers first: a pattern is the train
 * itself saying where it stops, and nothing in the bundle outranks that.
 */
export function stopsAt({
  departures = [],
  destinationBoard = null,
  destinationStopId,
  originStopId = null,
  patterns = null,
  windowMinutes = 120,
  now = Date.now(),
} = {}) {
  const out = new Map();

  const dest = Number(destinationStopId);
  if (!Number.isFinite(dest) || dest <= 0) return out;
  // Standing at it. There is no question to answer.
  if (originStopId != null && Number(originStopId) === dest) return out;

  // NULL IS NOT AN EMPTY BOARD, and the difference is the whole safety of this
  // function. An empty array means "that station has no trains in the window",
  // from which a run's absence really does mean express. Null means the board
  // was not read — the request failed, or it has not landed yet — and a run's
  // absence from a board nobody fetched means nothing whatsoever. So the free
  // verdicts still answer and the intersection does not: a board painted while
  // its partner is still in flight must not flash "doesn't stop there" across
  // half its rows and then take it back.
  const intersectable = Array.isArray(destinationBoard);

  // Run ref → EVERY time it leaves the destination, earliest first. A departure
  // from the far station is an arrival at it plus a dwell, which is under a
  // minute and is not worth modelling: the number on the row is the minute
  // somebody gets off.
  //
  // ALL of them, where this used to keep only the earliest — because the
  // earliest is routinely a stop the train has already made. A Belgrave
  // service passes Blackburn inbound at 12:48, reaches Richmond at 13:16 and
  // then goes round the city loop; a board at Richmond took that 12:48 and
  // printed "leaves 13:16, BLACKBURN 12:48", an arrival twenty-eight minutes
  // before its own departure. The run ref is right, the row is real, and the
  // stop is in the train's past. `arrivalAfter` below is what that costs.
  //
  // UNDER EVERY REF THE ROW ANSWERS TO, which is what the tunnel costs. The
  // far board files a through service under the run it BECOMES, so the Sunbury
  // row at State Library carries 990141 where the Carnegie row that is the same
  // train carries 967138 and advertises 990141 as its distributor. Filed under
  // one ref each, the two boards never met and the citybound half of Carnegie
  // was called express at a station it calls at every eight minutes.
  const arrivals = new Map();
  for (const row of destinationBoard ?? []) {
    const at = timeOf(row);
    if (!Number.isFinite(at)) continue;
    for (const ref of refsOf(row)) {
      const held = arrivals.get(ref);
      if (held) held.push(at); else arrivals.set(ref, [at]);
    }
  }
  for (const times of arrivals.values()) times.sort((a, b) => a - b);

  // HOW FAR THE FAR BOARD ACTUALLY REACHES, not how far it was asked to. A
  // board comes back capped at a number of rows, and late at night Anzac's six
  // rows covered twenty minutes of a two-hour window: every citybound train
  // after that was "absent", and absence inside the window read as EXPRESS, so
  // the whole of Carnegie went dim under "Nothing on this board stops at Anzac"
  // while those trains stop there. A train the far board could not have shown
  // is not evidence of anything.
  // An EMPTY far board is still the whole window: that station really has no
  // trains in it, which is the case the null/empty distinction above is for.
  const farTimes = (destinationBoard ?? []).map(timeOf).filter(Number.isFinite);
  const window = now + Number(windowMinutes) * 60_000;
  const horizon = farTimes.length ? Math.min(window, Math.max(...farTimes)) : window;
  const iso = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString() : null);

  /**
   * The first time this run leaves the destination AFTER it leaves here.
   *
   * ONLY A STOP THE TRAIN HAS YET TO MAKE COUNTS. A loop service calls at the
   * destination on its way in, comes to the platform somebody is standing on,
   * and calls there again an hour later; both rows are on the far board under
   * one ref, and only the second one is an arrival anybody can ride to. The
   * later one is not special-cased away either — it is a real option, just a
   * slow one, and the board orders it against the direct train on its own
   * merits.
   *
   * `ARRIVAL_FLOOR_MS` is the minute that keeps "arrives the instant it
   * leaves" out: a far-board row inside sixty seconds of this departure is the
   * same platform or a clock skew, never a journey.
   */
  const arrivalAfter = (refs, departsAt) => {
    const times = refs.flatMap((ref) => arrivals.get(ref) ?? []).sort((a, b) => a - b);
    if (!times.length) return null;
    if (!Number.isFinite(departsAt)) return times[0];
    return times.find((t) => t > departsAt + ARRIVAL_FLOOR_MS) ?? null;
  };

  for (const d of departures ?? []) {
    const refs = refsOf(d);
    const ref = refs[0] ?? '';
    if (!ref || out.has(ref) || d.cancelled) continue;

    // The departure is read BEFORE the arrival, because the arrival is chosen
    // relative to it — see `arrivalAfter` — and before the bundle, because
    // both of the answers below can beat the bundle's.
    //
    // KEYED ON THE ROW'S OWN REF, matched on every ref it answers to. The
    // drawing code has `runRef` in its hand and nothing else, so that is the
    // key; the handover ref is how the far board is found, not how the answer
    // is filed.
    const departsAt = timeOf(d);
    const boardArrival = arrivalAfter(refs, departsAt);

    // 0 ── THE TRAIN'S OWN PATTERN, where the caller had a reason to fetch one.
    // It outranks everything: a run that lists the destination among its stops
    // calls there, whatever a line's stop list says about where that line goes.
    const pattern = patternStops(patterns, ref);
    if (pattern) {
      out.set(ref, patternVerdict(pattern, {
        dest, originStopId, departure: d, boardArrival, iso,
      }));
      continue;
    }

    // 1 ── the bundle's free answer.
    const routes = routesOf(d);
    const serving = routes.find((routeId) => indexOn(lineStops(routeId), dest) >= 0) ?? null;

    // 0.5 ── THE PUBLISHED TIMETABLE, asked by the server about this very train.
    //
    // The board's request carried `to=<destination>`, and the server matched
    // the row to a timetable trip and read the destination off THAT TRIP. It
    // ranks just under the train's own pattern and above everything below,
    // because it answers what the two-board check below only infers: the
    // tunnel is one trip in the timetable (Sunbury to East Pakenham holds
    // Carnegie and State Library on one line of times) however many runs PTV
    // splits it into, and it is not limited by how far the far board reaches.
    // The live checks still run after this (`applyPatternChecks`), so a
    // cancellation or a skipped stop on the nearest trains can overrule it.
    const timetabled = timetableVerdict(d, dest, serving, iso);
    if (timetabled) {
      out.set(ref, timetabled);
      continue;
    }
    if (serving == null) {
      // …unless the destination's own board has this very run on it, later
      // than this departure. That is the train being seen at the destination,
      // and it beats a stop list that does not have the station on the line.
      // THE CASE IS REAL: a football special out of Richmond to Dandenong runs
      // via South Yarra, which routes 4 and 11 no longer list, and this branch
      // used to tell somebody standing at Richmond that the train pulling in
      // does not go where they can watch it go.
      if (boardArrival != null) {
        out.set(ref, { verdict: 'stops', arrivesAt: iso(boardArrival), estimated: false });
        continue;
      }
      out.set(ref, mightContinueTo(d, dest)
        ? { verdict: 'unknown', arrivesAt: null, estimated: false }
        : { verdict: 'elsewhere', arrivesAt: null, estimated: false });
      continue;
    }

    // Whether the far board carried this run at all, usable time or not. A run
    // that is ON that board but only in the past HAS stopped there, so its
    // absence of a future row is not evidence that it skips the place — see
    // the last case below, where saying "express" on that evidence would be
    // the board telling somebody their train does not stop where it just did.
    const seenFar = refs.some((r) => arrivals.has(r));

    // WHERE THE PASSENGER CAN GET TO on this train, which is the whole of the
    // journey and not the end of this run. Null where the bundle cannot place
    // the train, and the answers below then read exactly as they did before it
    // existed.
    const journey = journeyAhead(d, originStopId);
    const onward = journey ? journey.ahead.includes(dest) : false;
    const maybeOnward = journey ? journey.beyond.includes(dest) : false;

    // The timetabled reckoning, used twice below and worth having once.
    const estimatedArrival = Number.isFinite(departsAt)
      ? (() => {
        const seconds = journeySeconds({ routeId: serving, fromStopId: originStopId, toStopId: dest });
        return seconds == null ? null : departsAt + seconds * 1000;
      })()
      : null;

    // 2 ── it ends there. The one case the two boards cannot see: a service
    // that terminates at Blackburn never DEPARTS Blackburn, so it is missing
    // from that station's board for the most innocent reason there is. The far
    // board may still carry it — a terminating run often forms the next
    // service back — and its own time wins where it does.
    if (Number(d.finalStopId) === dest) {
      out.set(ref, boardArrival != null
        ? { verdict: 'stops', arrivesAt: iso(boardArrival), estimated: false }
        : { verdict: 'stops', arrivesAt: iso(estimatedArrival), estimated: estimatedArrival != null });
      continue;
    }

    // 3 ── it skips nothing, and yours is on the way.
    //
    // `ahead` ONLY, never `beyond`. A train that skips nothing calls at every
    // stop it passes, and the stops it passes on its own line are known exactly
    // — but where the continuation run ends is not published on this row, so a
    // station far out on the line it becomes is a station this train may
    // terminate short of. The far board is what settles those, below.
    if (expressCount(d) === 0 && onward) {
      out.set(ref, boardArrival != null
        ? { verdict: 'stops', arrivesAt: iso(boardArrival), estimated: false }
        // No row on the far board — it is beyond that board's horizon, or the
        // board simply was not fetched. The train still stops there, and the
        // timetable still says roughly when, so the time is given and marked.
        : { verdict: 'stops', arrivesAt: iso(estimatedArrival), estimated: estimatedArrival != null });
      continue;
    }

    // 4 ── two boards, intersected.
    if (boardArrival != null) {
      out.set(ref, { verdict: 'stops', arrivesAt: iso(boardArrival), estimated: false });
      continue;
    }

    // 5 ── the journey is known, and yours is not on it.
    //
    // THE ONE NEGATIVE THE BUNDLE CAN PROVE, and it costs no request at all: a
    // train travelling away from a station does not arrive at it. An East
    // Pakenham service at Carnegie has called at State Library already, under
    // the ref of the run it took over from, and everything ahead of it is
    // suburbs. The board used to leave those rows bright — the far board had
    // the run on it, in the past, which read as "say nothing" — while dimming
    // the citybound trains that were the only ones going there.
    //
    // THREE THINGS HOLD IT BACK, and each is a real case:
    //
    //   * A RUN THAT ENDS IN THE CITY may be going round the loop, and the
    //     bundle's stop list puts the loop stations on the far side of its
    //     terminus. That is the Belgrave service reaching Richmond at 13:16
    //     and then calling at four city stations this would have called
    //     skipped. `looping` refuses the whole question for those.
    //   * THE CONTINUATION'S OWN STOPS, which is `beyond`: a citybound train
    //     at Carnegie reaches State Library one stop PAST its handover at Town
    //     Hall, and the reach of the run it becomes is not published here.
    //   * A LINE THIS ONE SOMETIMES BECOMES without PTV saying so today, which
    //     is what `mightContinueTo` has always guarded.
    //
    // And the far board above outranks it, so a train seen at the destination
    // later than it leaves here is a stopper whatever the stop list thinks.
    if (journey && !journey.looping && !onward && !maybeOnward && !mightContinueTo(d, dest)) {
      out.set(ref, { verdict: 'express', arrivesAt: null, estimated: false });
      continue;
    }

    // Absent — or there was no far board to be absent from.
    if (!intersectable) {
      out.set(ref, { verdict: 'unknown', arrivesAt: null, estimated: false });
      continue;
    }

    // On that board, but only behind us. It stops there — it already has —
    // so the one thing that must not be said is that it does not.
    if (seenFar) {
      out.set(ref, { verdict: 'unknown', arrivesAt: null, estimated: false });
      continue;
    }

    // A through-runner changes run ref somewhere in the city — PTV models the
    // tunnel as two runs joined by an interchange — so its absence from a far
    // board can be the ref changing, not the train skipping. Never "express"
    // on that evidence: at Carnegie, every tunnel train to Watergardens was
    // being called "doesn't stop at State Library" when it stops there daily.
    //
    // ONLY WHERE THE HANDOVER REF IS MISSING now. Where PTV advertised the
    // distributor, `refsOf` above matched the far board on it and the two
    // boards really did meet, so an absence is an absence. Where it did not —
    // a payload without the Run expansion, a run whose terminus the bundle
    // places on the line it becomes and nothing more — there was never a ref
    // in common and the silence means nothing.
    if (journey?.handsOver && !d.throughRunRef) {
      out.set(ref, { verdict: 'unknown', arrivesAt: null, estimated: false });
      continue;
    }

    // A STATION ON THE FAR HALF OF THE JOURNEY is past the point where this
    // run hands over, and the horizon test below cannot speak for it: the
    // reckoning it measures runs along THIS line, and the stretch that matters
    // belongs to the run this train becomes, which has its own place in the far
    // board's window and its own reasons to be outside it. Silence about a
    // continuation stop is silence.
    if (maybeOnward) {
      out.set(ref, { verdict: 'unknown', arrivesAt: null, estimated: false });
      continue;
    }

    // Which means express only if the far board reached far enough ahead to
    // have carried it.
    const padded = paddedJourneySeconds(
      journeySeconds({ routeId: serving, fromStopId: originStopId, toStopId: dest }),
    );
    const wouldArriveBy = Number.isFinite(departsAt) && padded != null
      ? departsAt + padded * 1000
      : null;

    out.set(ref, wouldArriveBy != null && wouldArriveBy <= horizon
      ? { verdict: 'express', arrivesAt: null, estimated: false }
      : { verdict: 'unknown', arrivesAt: null, estimated: false });
  }

  return out;
}

// ── Checking the claims the board is about to make ───────────────────────────
//
// The verdicts above are ordered by cost, and the cheap ones are certain about
// a LINE and can be wrong about a TRAIN: `express_stop_count` and the bundle's
// stop order say a train from Parliament calls at Union, and the run itself —
// which is the ground truth, as `src/journey.js` says at the top — leaves it
// out. A board that puts that train at the head of "going there" has sent
// somebody to a platform for a train that will not stop.
//
// Never a pattern per row, so the check is aimed at the few rows that matter:
// the ones the board is CLAIMING, soonest first. A claim is the only verdict
// that costs somebody a train when it is wrong.

/** How many claimed rows one board may check against their own patterns. */
export const CLAIM_CHECK_MAX = 4;

/**
 * The rows whose verdict is "stops", soonest first, that have not been read
 * against their own pattern yet.
 *
 * `skip` is the refs already patterned. A cancelled row and a row with no run
 * ref are not claims, and the order is the board's own.
 */
export function claimedRows(departures, verdicts, { skip = new Set(), limit = CLAIM_CHECK_MAX } = {}) {
  const out = [];
  for (const d of departures ?? []) {
    const ref = d?.runRef == null ? '' : String(d.runRef);
    if (!ref || d.cancelled || skip.has(ref)) continue;
    if (verdicts?.get?.(ref)?.verdict !== 'stops') continue;
    out.push(d);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * What a pattern check is allowed to change.
 *
 * ONLY A CONTRADICTION. A pattern that says the train skips your stop, or does
 * not go there, replaces the claim. A pattern that agrees, or one that cannot
 * speak — the run hands over in the city and the destination is past the join,
 * which `patternVerdict` answers "unknown" — leaves the earlier verdict alone,
 * because "the pattern is silent" is not "the train does not stop", and
 * downgrading a Carnegie train to State Library on that evidence would remove
 * the trains the Metro Tunnel was built for.
 *
 * Returns a new Map; `prior` is not touched.
 */
export function applyPatternChecks(prior, checked, refs) {
  const out = new Map(prior instanceof Map ? prior : []);
  for (const ref of refs ?? []) {
    const verdict = checked?.get?.(String(ref));
    if (verdict && (verdict.verdict === 'express' || verdict.verdict === 'elsewhere')) {
      out.set(String(ref), verdict);
    }
  }
  return out;
}
